import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { SEO } from '../components/SEO';
import { collectiveReadings } from '../data/mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CollectiveApril2026Page = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        email: email,
        source: 'collective-april-2026'
      });
      setSubscribed(true);
      setEmail('');
      toast.success('You\'re subscribed', {
        description: 'You\'ll receive each month\'s reflection in your inbox.'
      });
    } catch (error) {
      toast.error('Something went wrong', {
        description: 'Please try again.'
      });
    }
    setIsSubmitting(false);
  };

  // Get previous readings for archive (exclude current)
  const previousReadings = collectiveReadings.filter(r => r.slug !== 'april-2026');

  return (
    <div className="bg-[var(--color-charcoal)]">
      <SEO 
        title="April 2026 Collective Reflection"
        description="April 2026 reflection exploring truth, alignment, and conscious choice."
        path="/collective/april-2026"
      />

      {/* Full-Width Hero - Softer tone */}
      <section className="relative min-h-[70vh] flex items-end">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1665518337917-97ab2ce2b793?w=1920&q=80"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-charcoal)] via-[var(--color-charcoal)]/60 to-transparent" />
        </div>
        
        <div className="relative w-full pb-16 pt-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1 }}
            >
              <Link 
                to="/collective" 
                className="inline-flex items-center gap-2 text-[var(--color-gold)] text-sm tracking-[0.2em] uppercase mb-8 hover:opacity-80 transition-opacity"
              >
                <ArrowLeft className="w-4 h-4" />
                The Collective
              </Link>
              
              <p className="text-[var(--color-gold)] text-lg tracking-[0.2em] uppercase mb-4">
                April 2026
              </p>
              
              <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl max-w-4xl leading-[1.05]">
                Truth, Choice, and Inner Alignment
              </h1>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Before You Read - Softer accent */}
      <section className="py-16 bg-[var(--color-dark-purple)]/10 border-y border-[var(--color-dark-purple)]/15">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <p className="text-[var(--color-gold)] text-lg mb-6 font-serif">What is true will ask to be chosen.</p>
            <p className="text-[var(--color-cream)]/70 leading-[2] text-lg italic">
              Pause for a moment.<br />
              Take a slow breath and let your body settle into itself.<br /><br />
              This is not a prediction. It is a mirror.<br />
              Take what resonates. Leave the rest.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <article className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          
          {/* The Energy of April */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-24"
          >
            <div className="lg:col-span-4">
              <h2 className="font-serif text-3xl md:text-4xl text-[var(--color-gold)] sticky top-32">
                The Energy of April
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-6 text-[var(--color-cream)]/75 text-lg leading-[1.9]">
              <p>April moves differently.</p>
              <p>There is less urgency, but more clarity. A quieter current, but one that asks for precision.</p>
              <p>If March brought movement, April brings awareness. It asks you to look at your life without softening the edges of what you see.</p>
              <p className="text-[var(--color-cream)]">The Lovers sit at the centre of this month. Not as romance, but as alignment. The place where different parts of you meet, and where a choice begins to take shape.</p>
              <p>Not a reactive choice. Not one made in haste.</p>
              <p>But one that becomes clear when you stop looking away.</p>
              <p>Alongside this comes an insistence on truth.</p>
              <p>Things you may have sensed for some time, but not fully acknowledged. Patterns you have noticed, but perhaps explained away. April does not rush you, but it does not let you remain in half-knowing either.</p>
              <p className="text-[var(--color-cream)]">There is a quiet clarity available now.</p>
              <p className="text-[var(--color-cream)] text-xl font-serif">And with it, the understanding that honesty changes the direction of things.</p>
            </div>
          </motion.section>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-24">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[var(--color-gold)]/20 to-transparent" />
          </div>

          {/* What Is Being Released */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-24"
          >
            <div className="lg:col-span-4">
              <h2 className="font-serif text-3xl md:text-4xl text-[var(--color-bottle-green-light)] sticky top-32">
                What Is Being Released
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-6 text-[var(--color-cream)]/75 text-lg leading-[1.9]">
              <p>Some patterns will become harder to ignore.</p>
              <p>Old stories, especially the ones shaped by self-criticism or subtle forms of victimhood.</p>
              <p>Narratives that feel familiar, even comforting at times, but keep you in repetition.</p>
              <p>You may also notice a certain rigidity. A reluctance to see differently. A sense of already knowing, even when something deeper is asking to be reconsidered.</p>
              <p className="text-[var(--color-cream)]">April does not confront harshly.</p>
              <p className="text-[var(--color-cream)] text-xl font-serif">It reveals.</p>
              <p>And in that revealing, there is a shift.</p>
              <p>You may begin to see your own role in patterns you once felt caught in. Not as blame, but as clarity.</p>
              <p>The same themes may appear around you as well. In conversations, in relationships, even in the wider world. A defensiveness, a holding of position, a difficulty in admitting what is true.</p>
              <p className="text-[var(--color-cream)]">You are being asked to move differently.</p>
              <p>To remain open without losing your centre.</p>
              <p>To adjust without abandoning what matters.</p>
            </div>
          </motion.section>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-24">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[var(--color-gold)]/20 to-transparent" />
          </div>

          {/* The Invitation */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-24"
          >
            <div className="lg:col-span-4">
              <h2 className="font-serif text-3xl md:text-4xl text-[var(--color-gold)] sticky top-32">
                The Invitation
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-6 text-[var(--color-cream)]/75 text-lg leading-[1.9]">
              <p>There is movement available this month.</p>
              <p>A sense that things can begin, or begin again. Aries energy continues to support that forward motion.</p>
              <p className="text-[var(--color-cream)]">But the invitation is not to rush.</p>
              <p className="text-[var(--color-cream)] text-xl font-serif">It is to choose with clarity.</p>
              <p>To notice where you are acting from habit, and where you are acting from alignment. To recognise the difference between what feels comfortable and what feels true.</p>
              <p>Clarity is not always gentle.</p>
              <p>But it brings simplicity.</p>
              <p className="text-[var(--color-cream)]">Once something is seen clearly, the next step becomes obvious.</p>
              <p>There is also a quieter layer to this month. A need for rest, but not the kind that delays. The kind that restores.</p>
              <p>Moments of pause that allow you to return to yourself before you move again.</p>
              <p className="text-[var(--color-cream)] text-xl font-serif">Balance is not something extra to consider. It is what allows you to sustain what you are building.</p>
            </div>
          </motion.section>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-24">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[var(--color-gold)]/20 to-transparent" />
          </div>

          {/* Self-Care & Embodiment */}
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-24"
          >
            <div className="lg:col-span-4">
              <h2 className="font-serif text-3xl md:text-4xl text-[var(--color-cream)]/60 sticky top-32">
                Self-Care & Embodiment
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-6 text-[var(--color-cream)]/75 text-lg leading-[1.9]">
              <p>April turns your attention to space.</p>
              <p>What you are holding on to. What surrounds you. What quietly shapes how you think and feel.</p>
              <p>There is value in clearing what has become unnecessary. Physical space, certainly. But also habits, patterns, even ways of relating that no longer feel aligned.</p>
              <p className="text-[var(--color-cream)]">Not everything needs to be carried forward.</p>
              <p>There is also a gentler reminder woven through this.</p>
              <p>To see yourself more clearly, without immediately reaching for what needs to be fixed.</p>
              <p className="text-[var(--color-cream)]">To recognise your own worth without making it conditional.</p>
              <p>Where have you been waiting to feel ready, or enough?</p>
              <p className="text-[var(--color-cream)] text-xl font-serif">Nothing new is required here. Only recognition.</p>
            </div>
          </motion.section>

        </div>
      </article>

      {/* Inquiry - Softer accent */}
      <section className="py-20 lg:py-28 bg-[var(--color-bottle-green)]/10 border-y border-[var(--color-bottle-green)]/15">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="font-serif text-2xl text-[var(--color-gold)] mb-8">Inquiry</h2>
            <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-[var(--color-cream)] leading-[1.4] mb-6">
              Where in my life am I choosing comfort over truth?
            </p>
            <p className="text-[var(--color-cream)]/60 text-lg leading-relaxed max-w-2xl mx-auto">
              Let the answer come without forcing it.<br />
              Then take one step that reflects what you already know.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Closing */}
      <section className="py-20 lg:py-28">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            <h2 className="font-serif text-2xl text-[var(--color-gold)] mb-8">Closing</h2>
            <p className="text-xl md:text-2xl text-[var(--color-cream)]/80 leading-[1.8]">
              April is not loud.
            </p>
            <p className="text-xl md:text-2xl text-[var(--color-cream)]/80 leading-[1.8]">
              It asks for a different kind of attention. The kind that comes from stillness, from honesty, from being willing to see what is already there.
            </p>
            <p className="text-xl md:text-2xl text-[var(--color-cream)]/80 leading-[1.8]">
              You do not need to have everything resolved.
            </p>
            <p className="text-xl md:text-2xl text-[var(--color-cream)]/80 leading-[1.8]">
              But you do need to be willing to see clearly.
            </p>
            <p className="font-serif text-2xl md:text-3xl text-[var(--color-cream)] mt-8">
              From there, the choice will follow.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Continue the Work */}
      <section className="py-20 bg-[var(--color-charcoal-light)]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {/* Tarot Session */}
              <div>
                <h3 className="font-serif text-2xl text-[var(--color-cream)] mb-4">Continue the Work</h3>
                <p className="text-[var(--color-cream)]/70 leading-[1.8] mb-6">
                  If this month's reflection stirred something you would like to explore more deeply, you are welcome to book a reflective tarot session.
                </p>
                <p className="text-[var(--color-cream)]/60 leading-[1.8] mb-6 text-sm">
                  Tarot here is not predictive. It is a grounded conversation that supports clarity, emotional resilience, and conscious choice.
                </p>
                <Link
                  to="/tarot"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--color-gold)] text-[var(--color-charcoal)] font-medium rounded-full hover:bg-[var(--color-gold-light)] transition-colors"
                >
                  Book a Tarot Session
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              {/* Newsletter */}
              <div>
                <h3 className="font-serif text-2xl text-[var(--color-cream)] mb-4">Monthly Reflections</h3>
                <p className="text-[var(--color-cream)]/70 leading-[1.8] mb-6">
                  Receive each month's Collective Reflection directly in your inbox.
                </p>
                
                {subscribed ? (
                  <p className="text-[var(--color-gold)]">Thank you. You'll receive the next reflection in your inbox.</p>
                ) : (
                  <form onSubmit={handleNewsletterSubmit} className="space-y-4">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Your email address"
                      required
                      className="w-full px-5 py-3 bg-[var(--color-charcoal)] border border-[var(--color-dark-purple)]/30 rounded-lg text-[var(--color-cream)] placeholder:text-[var(--color-cream)]/40 focus:outline-none focus:border-[var(--color-gold)]/50 transition-colors"
                      data-testid="collective-april-email"
                    />
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full px-6 py-3 bg-[var(--color-gold)] text-[var(--color-charcoal)] font-medium rounded-lg hover:bg-[var(--color-gold-light)] transition-all duration-300 disabled:opacity-50"
                      data-testid="collective-april-submit"
                    >
                      {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                    </button>
                    <p className="text-xs text-[var(--color-cream)]/40 text-center">
                      One reflection per month. No noise.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Previous Reflections */}
      <section className="py-12 border-t border-[var(--color-dark-purple)]/20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <details className="group">
            <summary className="flex items-center justify-between cursor-pointer py-4 text-[var(--color-cream)]/60 hover:text-[var(--color-cream)] transition-colors">
              <span className="font-serif text-lg">Previous Collective Reflections</span>
              <ChevronDown className="w-5 h-5 group-open:rotate-180 transition-transform" />
            </summary>
            <div className="pt-4 pb-8 space-y-4">
              {previousReadings.length > 0 ? (
                previousReadings.map((reading) => (
                  <Link
                    key={reading.id}
                    to={`/collective/${reading.slug}`}
                    className="block p-4 bg-[var(--color-charcoal-light)] rounded-lg hover:bg-[var(--color-dark-purple)]/20 transition-colors"
                  >
                    <p className="text-[var(--color-gold)] text-sm mb-1">{reading.month}</p>
                    <p className="text-[var(--color-cream)]">{reading.title}</p>
                  </Link>
                ))
              ) : (
                <p className="text-[var(--color-cream)]/50 italic">No previous reflections yet.</p>
              )}
            </div>
          </details>
        </div>
      </section>
    </div>
  );
};
